/**
 * Store (Zustand) das camadas vetoriais + codec do link (chave `m`).
 *
 * O codec é registrado no carregamento deste módulo — basta o app-core
 * importar `useVectorLayers` ou `VectorPanel` (ambos importam este arquivo)
 * antes de ler a URL.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import { DEFAULT_PRESET, defaultVisibility, getLayerDef } from './catalog';
import { pruneOverride, resolveStyle } from './styleConfig';
import { decodeVectorState, encodeVectorState } from './urlState';
import type {
  DataLayerId,
  DataLoadState,
  LayerStyle,
  LoadStatus,
  Manifest,
  PresetId,
  StyleOverride,
  VectorLayerId,
} from './types';

export interface VectorState {
  /** Camadas ligadas. */
  visible: Record<VectorLayerId, boolean>;
  /**
   * Seleção que estava ligada quando o mapa inteiro foi desligado pelo
   * trilho, para religar a mesma seleção. null = alguma camada ligada.
   */
  visibleBeforeOff: Record<VectorLayerId, boolean> | null;
  /** Predefinição global de aparência. */
  preset: PresetId;
  /** Personalizações por camada (só o que difere da base da predefinição). */
  overrides: Partial<Record<VectorLayerId, StyleOverride>>;
  /** Destacar no mapa a área clicada (município, TI, UC). */
  highlight: boolean;

  manifest: Manifest | null;
  manifestState: DataLoadState;
  /** Estado de carregamento de cada arquivo de dados. */
  data: Partial<Record<DataLayerId, DataLoadState>>;

  setVisible: (id: VectorLayerId, visible: boolean) => void;
  /**
   * Interruptor do trilho: desliga todas as camadas do mapa ou religa a
   * seleção anterior (o padrão do catálogo, se nenhuma estava ligada).
   */
  setAllVisible: (on: boolean) => void;
  /** Altera campos de estilo de uma camada (o que voltar ao padrão é descartado). */
  setStyle: (id: VectorLayerId, patch: Partial<LayerStyle>) => void;
  /** "Restaurar padrão" de uma camada (volta à base da predefinição atual). */
  resetStyle: (id: VectorLayerId) => void;
  /** Aplica uma predefinição e descarta todas as personalizações. */
  applyPreset: (preset: PresetId) => void;
  setHighlight: (on: boolean) => void;

  // Uso interno do carregador (loader.ts).
  setManifestState: (state: DataLoadState, manifest?: Manifest | null) => void;
  setDataState: (id: DataLayerId, state: DataLoadState) => void;
}

export const useVectorStore = create<VectorState>()((set) => ({
  visible: defaultVisibility(),
  visibleBeforeOff: null,
  preset: DEFAULT_PRESET,
  overrides: {},
  highlight: true,

  manifest: null,
  manifestState: { status: 'idle' },
  data: {},

  setVisible: (id, visible) =>
    set((s) => (s.visible[id] === visible ? s : { visible: { ...s.visible, [id]: visible } })),

  setAllVisible: (on) =>
    set((s) => {
      const anyOn = Object.values(s.visible).some(Boolean);
      if (!on) {
        if (!anyOn) return s;
        const visible = { ...s.visible };
        for (const id of Object.keys(visible) as VectorLayerId[]) visible[id] = false;
        return { visible, visibleBeforeOff: s.visible };
      }
      if (anyOn) return s;
      const before = s.visibleBeforeOff;
      const restore = before && Object.values(before).some(Boolean) ? before : defaultVisibility();
      return { visible: { ...restore }, visibleBeforeOff: null };
    }),

  setStyle: (id, patch) =>
    set((s) => {
      const next = pruneOverride(id, s.preset, { ...s.overrides[id], ...patch });
      const overrides = { ...s.overrides };
      if (next) overrides[id] = next;
      else delete overrides[id];
      return { overrides };
    }),

  resetStyle: (id) =>
    set((s) => {
      if (!s.overrides[id]) return s;
      const overrides = { ...s.overrides };
      delete overrides[id];
      return { overrides };
    }),

  applyPreset: (preset) => set({ preset, overrides: {} }),

  setHighlight: (highlight) => set({ highlight }),

  setManifestState: (manifestState, manifest) =>
    set(manifest === undefined ? { manifestState } : { manifestState, manifest }),

  setDataState: (id, state) => set((s) => ({ data: { ...s.data, [id]: state } })),
}));

// ---------------------------------------------------------------------------
// Seletores auxiliares
// ---------------------------------------------------------------------------

/** Estilo efetivo de uma camada no estado dado. */
export function selectStyle(s: Pick<VectorState, 'preset' | 'overrides'>, id: VectorLayerId): LayerStyle {
  return resolveStyle(id, s.preset, s.overrides[id]);
}

/**
 * Estado de carregamento combinado de uma camada de mapa (várias fontes):
 * erro > carregando > pronto > indisponível > ocioso.
 */
export function selectLayerStatus(s: Pick<VectorState, 'data' | 'manifest'>, id: VectorLayerId): LoadStatus {
  const def = getLayerDef(id);
  const states = def.data.map((d) => s.data[d]?.status ?? (s.manifest && !s.manifest.layers[d] ? 'missing' : 'idle'));
  if (states.includes('error')) return 'error';
  if (states.includes('loading')) return 'loading';
  if (states.every((st) => st === 'missing')) return 'missing';
  if (states.every((st) => st === 'ready' || st === 'missing')) return 'ready';
  return 'idle';
}

/** Soma dos tamanhos (bytes) dos arquivos da camada, pelo manifest. */
export function selectLayerBytes(s: Pick<VectorState, 'manifest'>, id: VectorLayerId): number | null {
  if (!s.manifest) return null;
  let total = 0;
  let any = false;
  for (const d of getLayerDef(id).data) {
    const b = s.manifest.layers[d]?.bytes;
    if (typeof b === 'number') {
      total += b;
      any = true;
    }
  }
  return any ? total : null;
}

// ---------------------------------------------------------------------------
// Link compartilhável (chave `m`)
// ---------------------------------------------------------------------------

registerUrlCodec({
  key: 'm',
  encode: () => {
    const { visible, preset, overrides } = useVectorStore.getState();
    return encodeVectorState({ visible, preset, overrides });
  },
  decode: (value) => {
    try {
      const parsed = decodeVectorState(value);
      if (!parsed) return;
      useVectorStore.setState((s) => ({
        visible: parsed.visible ? { ...s.visible, ...parsed.visible } : s.visible,
        preset: parsed.preset ?? s.preset,
        overrides: parsed.overrides ?? (parsed.preset ? {} : s.overrides),
      }));
    } catch {
      // Valor inválido na URL: mantém o padrão.
    }
  },
  subscribe: (onChange) =>
    useVectorStore.subscribe((s, prev) => {
      if (s.visible !== prev.visible || s.preset !== prev.preset || s.overrides !== prev.overrides) onChange();
    }),
});
